import React from "react";
import { useTheme } from "@hooks/useTheme";
import { useTranslation } from "react-i18next";
import { VulnerabilityBadge } from "@components/UI/Badge";
import { MatchIcon, UnMatchIcon } from "@components/Icons";
import TableWithPagination from "@components/Tables/TableWithPagination";
import { ComponentHeader } from "@components/Tables/ComponentHeader";
import { useEffect, useState } from "react";
import useVulnerabilityPolicy from "@hooks/useVulnerabilityPolicy";
import ComponentConflictStatusGuideModal from "@components/UI/ComponentConflictStatusGuideModal";
import { VulnerabilityDetailModal, ComponentInformationModal, LicenseInformationModal } from "@components/UI";
import { useMutationData } from "@hooks/useMutationData";

const ComponentListComparisonResult = ({
  componentData1,
  componentData2,
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { vulnerabilityLevel } = useVulnerabilityPolicy();
  const [isVulnerabilityModalOpen, setIsVulnerabilityModalOpen] = useState(false);
  const [selectedVulnerabilityData, setSelectedVulnerabilityData] = useState(null);
  const [isComponentModalOpen, setIsComponentModalOpen] = useState(false);
  const [selectedComponentData, setSelectedComponentData] = useState(null);
  const [isLicenseInformationModalOpen, setIsLicenseInformationModalOpen] = useState(false);
  const [selectedLicenseInformationData, setSelectedLicenseInformationData] = useState(null);

  // Helper function to create a unique key for comparison
  const createComponentKey = (component) => {
    const componentName = component.component || component.componentName || "";
    const componentLicense = component.componentLicense || component.licenseIdentifier || "";
    return `${componentName}-${component.componentVersion}-${componentLicense}`;
  };

  // Compare components and categorize them
  const compareComponents = () => {
    if (!componentData1 || !componentData2) {
      return {
        commonComponents: [],
        onlyInVerification1: componentData1 || [],
        onlyInVerification2: componentData2 || []
      };
    }

    const set1 = new Set(componentData1.map(createComponentKey));
    const set2 = new Set(componentData2.map(createComponentKey));


    const commonComponents = componentData1.filter(comp1 =>
      set2.has(createComponentKey(comp1))
    );

    const onlyInVerification1 = componentData1.filter(comp1 =>
      !set2.has(createComponentKey(comp1))
    );

    const onlyInVerification2 = componentData2.filter(comp2 =>
      !set1.has(createComponentKey(comp2))
    );

    return {
      commonComponents,
      onlyInVerification1,
      onlyInVerification2
    };
  };

  const { commonComponents, onlyInVerification1, onlyInVerification2 } = compareComponents();

  const getVulnerabilityDataMutation = useMutationData(
    (levelCode) => `/vulnerability_policy/level_code/${levelCode}`,
    { method: "GET" },
    {
      onSuccess: (response, levelCode) => {
        setSelectedVulnerabilityData({ ...response, vulnerabilityLevel: levelCode });
        setIsVulnerabilityModalOpen(true);
      },
      onError: (error) => {
        console.error("Error:", error);
      },
    },
  );

  const handleVulnerabilityClick = async (vulnerabilityLevel) => {
    if (vulnerabilityLevel) {
      await getVulnerabilityDataMutation.mutateAsync(vulnerabilityLevel);
    }
  };

  // Component list table configuration
  const componentListColumns = [
    "component",
    "severity",
    "vulnerabilityLevel",
  ];

  const componentListColumnConfig = {
    component: {
      headerRender: () => <ComponentHeader/>,
      customRender: (value, row) => {
        const componentVersion = row.componentVersion || "";
        const licenseIdentifier = row.componentLicense || row.licenseIdentifier || row.licenseName;
        const isValidLicense = licenseIdentifier != null && licenseIdentifier.toUpperCase() !== "N/A";
        const isVersionLong = componentVersion.length > 15; // Threshold for truncation
        const componentName = row.component || row.componentName || "";

        const handleLicenseClick = (e) => {
          e.stopPropagation();
          if (isValidLicense) {
            setSelectedLicenseInformationData(licenseIdentifier);
            setIsLicenseInformationModalOpen(true);
          }
        };

        const handleComponentClick = (e) => {
          e.stopPropagation();
          setSelectedComponentData(row);
          setIsComponentModalOpen(true);
        };

        return (
          <div className="flex items-center gap-1.5 w-full min-w-0 h-8">
            <div className="flex-1 min-w-0 overflow-hidden">
              <div
                className={`text-sm font-medium ${colors.textComparisonPrimary} cursor-pointer ${colors.hoverTextAccentCyan} transition-colors truncate`}
                onClick={handleComponentClick}
                title={`${componentName} (${componentVersion})`}
              >
                <span className={`${colors.textPrimary}`}>{componentName}</span>
                <span className={colors.textComparisonMuted}>
                  {` (`}
                  {isVersionLong ? (
                    <span className="truncate max-w-[120px] inline-block align-bottom" title={componentVersion}>
                      {componentVersion}
                    </span>
                  ) : (
                    componentVersion
                  )}
                  {`)`}
                </span>
              </div>
              <div
                className={`mt-0.5 text-xs truncate ${isValidLicense ? `cursor-pointer ${colors.textComparisonLink} ${colors.hoverTextComparisonLink} transition-colors` : colors.textComparisonMuted}`}
                onClick={isValidLicense ? handleLicenseClick : undefined}
                title={licenseIdentifier || ""}
              >
                {licenseIdentifier || ""}
              </div>
            </div>
          </div>
        );
      },
      style: { width: "40%" },
    },
    severity: {
      headerRender: () => t("status.severity"),
      customRender: (value, row) => {
        if (!row.cvssScore) return <span className={`text-sm ${colors.textComparisonMuted}`}>---</span>;
        const displayValue = row.cvssScore.charAt(0).toUpperCase() + row.cvssScore.slice(1);
        return (
          <span className={`text-sm ${colors.textComparisonPrimary}`}>{displayValue}</span>
        );
      },
    },
    vulnerabilityLevel: {
      style: {
        whiteSpace: "normal",
        textAlign: "center",
        overflowWrap: "initial",
        textTransform: "uppercase"
      },
      headerRender: () => (
        <ComponentConflictStatusGuideModal
          title={t("vulnerability-policy.vulnerability-security-code")}
          colors={colors}
          t={t}
          showObligationLevel={false}
        />
      ),
      customRender: (value) => {
        const matchedData = vulnerabilityLevel.find(item => item.levelCode === value);
        if (matchedData) {
          return (
            <VulnerabilityBadge
              level={value}
              bgColor={matchedData.vulnerabilityLevel}
              className="font-extrabold"
              onClick={() => {
                handleVulnerabilityClick(value);
              }}
            />
          );
        } else {
          return <span className={`text-sm ${colors.textComparisonMuted}`}>---</span>;
        }
      },
    },
  };

  const ComparisonPanel = ({
    title,
    icon: Icon,
    iconColor,
    data
  }) => {
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Reset to page 1 when data changes
    useEffect(() => {
      setCurrentPage(1);
    }, [data?.length]);

    // Pagination calculations
    const totalPages = Math.ceil((data?.length || 0) / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = data?.slice(startIndex, endIndex) || [];

    return (
      <div className="relative">
        <div className={`flex items-center p-4 pt-0 rounded-xl ${colors.bgFulfillmentStripe} mb-0 h-20`}>
          <Icon size={28} className={`mr-2 ${iconColor}`} />
          <h3 className={`text-md font-semibold ${colors.textPrimary}`}>
            {title}
          </h3>
        </div>

        <div className="relative -mt-6">
          <TableWithPagination
            columns={componentListColumns}
            columnConfig={componentListColumnConfig}
            data={paginatedData}
            onRowClick={() => { }}
            loading={false}
            totalPages={totalPages}
            totalData={data?.length || 0}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            maxRowsPerPage={itemsPerPage}
            hasCheckbox={false}
            hasRadioButton={false}
            rowKey="componentSeq"
            shouldShowGoToPage={true}
            showPagination={true}
            showItemsPerPageSelector={true}
            onItemsPerPageChange={(value) => {
              setItemsPerPage(value);
              setCurrentPage(1);
            }}
            containerStyle={{ borderRadius: "16px" }}
            rowHeight={64}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="component-list-comparison-result">
      <div className="flex items-center justify-between">
        <h1 className={`text-md font-bold ${colors.textPrimary}`}>
          {t("verification-comparison.component-comparison")}
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Common Components Panel */}
        <ComparisonPanel
          title={t("verification-comparison.common-components")}
          icon={MatchIcon}
          iconColor={colors.textSuccessBright}
          data={commonComponents}
        />

        {/* Only in Verification 1 Panel */}
        <ComparisonPanel
          title={t("verification-comparison.only-in-verification-result-1")}
          icon={UnMatchIcon}
          iconColor={colors.textFailureBright}
          data={onlyInVerification1}
        />

        {/* Only in Verification 2 Panel */}
        <ComparisonPanel
          title={t("verification-comparison.only-in-verification-result-2")}
          icon={UnMatchIcon}
          iconColor={colors.textFailureBright}
          data={onlyInVerification2}
        />
      </div>
      <VulnerabilityDetailModal
        isOpen={isVulnerabilityModalOpen}
        onClose={() => {
          setIsVulnerabilityModalOpen(false);
          setSelectedVulnerabilityData(null);
        }}
        data={selectedVulnerabilityData}
      />
      <ComponentInformationModal
        show={isComponentModalOpen}
        onClose={() => {
          setIsComponentModalOpen(false);
          setSelectedComponentData(null);
        }}
        componentData={selectedComponentData}
      />
      <LicenseInformationModal
        show={isLicenseInformationModalOpen}
        onClose={() => {
          setIsLicenseInformationModalOpen(false);
          setSelectedLicenseInformationData(null);
        }}
        componentLicense={selectedLicenseInformationData}
      />
    </div>
  );
};

export default ComponentListComparisonResult;
